// src/components/Navbar.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { Menu, X, LogOut, LogIn, UserPlus, Zap, ChevronDown, Sparkles, Shield, Crown } from 'lucide-react';
import { logout } from '../redux/slices/userSlice';
import { fetchMySubscription } from '../redux/slices/paymentSlice';
import Logo from './Logo';
import UserAvatar from './Common/UserAvatar';
import { getPlanDetails } from '../utils/planUtils';

const Navbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { isAuthenticated, user } = useSelector((state) => state.user);
  const { subscription } = useSelector((state) => state.payment);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Fetch subscription on mount if authenticated
  useEffect(() => {
    if (isAuthenticated) {
      dispatch(fetchMySubscription());
    }
  }, [isAuthenticated, dispatch]);

  const renderPlanBadge = (mobile = false) => {
    const details = getPlanDetails(subscription?.plan?.name, subscription?.status);
    const planName = details.label;
    const isPro = planName.includes('Pro');

    if (isPro) {
      return (
        <div className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full ${mobile ? 'text-xs' : 'text-[10px]'} font-bold shadow-sm shadow-indigo-200/50 uppercase tracking-wide ${planName.includes('Yearly') ? 'bg-gradient-to-r from-blue-400 to-indigo-600' : 'bg-gradient-to-r from-emerald-400 to-teal-600'} text-white`}>
          <Crown className={`${mobile ? 'w-3.5 h-3.5' : 'w-3 h-3'} fill-white/20 text-white`} />
          <span className="drop-shadow-sm">{planName}</span>
        </div>
      );
    }

    return (
      <div className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-lg font-semibold ${mobile ? 'text-xs' : 'text-[10px]'} uppercase tracking-wider ${details.colors}`}>
        <span>{planName}</span>
      </div>
    );
  };

  const handleLogout = () => {
    dispatch(logout());
    window.location.href = '/';
    setIsMobileMenuOpen(false);
    setShowUserMenu(false);
  };

  const handleLogin = () => {
    navigate('/auth?mode=login');
    setIsMobileMenuOpen(false);
  };

  const handleRegister = () => {
    navigate('/auth?mode=register');
    setIsMobileMenuOpen(false);
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleNavClick = (path, sectionId = null) => {
    const target = sectionId ? `${path}#${sectionId}` : path;
    navigate(target, { replace: true });
    setIsMobileMenuOpen(false);
    if (location.pathname === path) {
      if (sectionId) {
        setTimeout(() => scrollToSection(sectionId), 100);
      } else {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }
  };

  const navLinks = [
    { name: 'Home', path: '/', section: null },
    { name: 'Services', path: '/', section: 'analysis-report' },
    { name: 'Subscription', path: '/', section: 'subscription-plans' },
  ];

  const isActive = (path, sectionId = null) => {
    if (path !== '/') return location.pathname === path;
    if (sectionId) {
      return location.pathname === path && location.hash === `#${sectionId}`;
    } else {
      return location.pathname === path && location.hash === '';
    }
  };

  const handleHomeClick = () => {
    handleNavClick('/', null);
  };

  return (
    <>
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${scrolled
        ? 'bg-white/80 backdrop-blur-xl shadow-2xl border-b border-gray-200/50'
        : 'bg-white/95 backdrop-blur-md shadow-lg border-b border-gray-100'
        }`}>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20 relative">
            {/* Enhanced Logo Section */}
            <div
              className="flex items-center cursor-pointer group"
              onClick={handleHomeClick}
            >
              <Logo className="h-10" />
            </div>

            {/* Desktop Navigation Links - Center */}
            <div className="hidden lg:flex items-center space-x-1">
              {navLinks.map((link) => (
                <button
                  key={link.section || 'home'}
                  onClick={() => handleNavClick(link.path, link.section)}
                  className={`relative px-6 py-2.5 font-semibold transition-all duration-300 rounded-xl group ${isActive(link.path, link.section)
                    ? 'text-white bg-gradient-to-r from-blue-600 to-purple-600 shadow-lg shadow-blue-500/30'
                    : 'text-gray-700 hover:text-blue-600 hover:bg-blue-50/50'
                    }`}
                >
                  <span className="relative z-10">{link.name}</span>
                  {!isActive(link.path, link.section) && (
                    <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-blue-600 to-purple-600 opacity-0 group-hover:opacity-10 transition-opacity duration-300"></div>
                  )}
                </button>
              ))}
            </div>

            {/* Enhanced Auth Section */}
            <div className="hidden lg:flex items-center space-x-3">
              {isAuthenticated ? (
                <>
                  {/* User Profile Dropdown */}
                  <div className="relative">
                    <button
                      onClick={() => setShowUserMenu(!showUserMenu)}
                      className="flex items-center space-x-3 px-4 py-2.5 bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl hover:from-gray-100 hover:to-gray-200 transition-all duration-300 border border-gray-200 shadow-sm hover:shadow-md group"
                    >
                      <UserAvatar 
                        user={user} 
                        size="md" 
                        indicator={true}
                      />
                      <div className="text-left hidden lg:block">
                        <p className="text-sm font-bold text-gray-900 leading-tight mb-0.5">{user?.full_name || user?.email}</p>
                        {renderPlanBadge()}
                      </div>
                      <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform duration-300 ${showUserMenu ? 'rotate-180' : ''}`} />
                    </button>

                    {/* Dropdown Menu */}
                    {showUserMenu && (
                      <div className="absolute right-0 mt-2 w-64 bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden z-50">
                        <div className="p-4 bg-gradient-to-r from-blue-600 to-purple-600">
                          <div className="flex items-center space-x-3">
                            <UserAvatar 
                              user={user} 
                              size="lg" 
                              avatarClassName="bg-white/20 backdrop-blur-sm shadow-none ring-0 border border-white/30"
                            />
                            <div>
                              <p className="text-sm font-bold text-white">{user?.full_name || user?.email}</p>
                              <div className="mt-1">
                                {renderPlanBadge()}
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="p-2">
                          <button
                            onClick={() => {
                              navigate('/dashboard');
                              setShowUserMenu(false);
                            }}
                            className="w-full flex items-center space-x-3 px-4 py-3 text-gray-700 hover:bg-blue-50 rounded-xl transition-colors duration-200"
                          >
                            <Zap className="w-4 h-4 text-blue-600" />
                            <span className="font-medium">Dashboard</span>
                          </button>
                          {user?.access_role === 'admin' && (
                            <button
                              onClick={() => {
                                navigate('/admin');
                                setShowUserMenu(false);
                              }}
                              className="w-full flex items-center space-x-3 px-4 py-3 text-gray-700 hover:bg-indigo-50 rounded-xl transition-colors duration-200 mt-1"
                            >
                              <Shield className="w-4 h-4 text-indigo-600" />
                              <span className="font-medium">Admin Portal</span>
                            </button>
                          )}
                          <button
                            onClick={handleLogout}
                            className="w-full flex items-center space-x-3 px-4 py-3 text-red-600 hover:bg-red-50 rounded-xl transition-colors duration-200 mt-1"
                          >
                            <LogOut className="w-4 h-4" />
                            <span className="font-medium">Logout</span>
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  <button
                    onClick={() => navigate('/dashboard')}
                    className="relative px-6 py-2.5 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-bold rounded-xl shadow-lg shadow-blue-500/30 hover:shadow-xl hover:shadow-blue-500/40 transition-all duration-300 overflow-hidden group"
                  >
                    <span className="relative z-10 flex items-center space-x-2">
                      <Zap className="w-4 h-4" />
                      <span>Dashboard</span>
                    </span>
                    <div className="absolute inset-0 bg-gradient-to-r from-purple-600 to-blue-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={handleLogin}
                    className="flex items-center space-x-2 px-6 py-2.5 text-gray-700 font-bold bg-white rounded-xl hover:bg-gray-50 transition-all duration-300 shadow-sm hover:shadow-md border-2 border-gray-200 hover:border-gray-300"
                  >
                    <LogIn className="w-4 h-4" />
                    <span>Login</span>
                  </button>
                  <button
                    onClick={handleRegister}
                    className="relative flex items-center space-x-2 px-6 py-2.5 bg-gradient-to-r from-blue-600 via-purple-600 to-blue-600 bg-[length:200%_auto] text-white rounded-xl font-bold shadow-lg shadow-blue-500/30 hover:shadow-xl hover:shadow-blue-500/40 transition-all duration-300 overflow-hidden group animate-gradient"
                  >
                    <UserPlus className="w-4 h-4 relative z-10" />
                    <span className="relative z-10">Register</span>
                    <Sparkles className="w-4 h-4 relative z-10 animate-pulse" />
                  </button>
                </>
              )}
            </div>

            {/* Enhanced Mobile Menu Button */}
            <div className="lg:hidden">
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="p-2.5 text-gray-700 hover:text-blue-600 transition-all duration-300 hover:bg-blue-50 rounded-xl"
              >
                {isMobileMenuOpen ? (
                  <X className="w-6 h-6 transition-transform duration-300 rotate-90" />
                ) : (
                  <Menu className="w-6 h-6 transition-transform duration-300" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Add this CSS for the gradient animation */}
        <style>{`
          @keyframes gradient {
            0% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
            100% { background-position: 0% 50%; }
          }
          .animate-gradient {
            animation: gradient 3s ease infinite;
          }
        `}</style>
      </nav>

      {/* Premium Mobile Menu - Rendered outside nav for proper fixed positioning */}
      {isMobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 top-20 z-[100] overflow-hidden">
          {/* Gradient Background with Blur */}
          <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-white to-blue-50"></div>

          {/* Decorative Elements */}
          <div className="absolute top-0 right-0 w-72 h-72 bg-gradient-to-br from-blue-400/20 to-purple-500/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-gradient-to-tr from-indigo-400/15 to-pink-400/15 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2"></div>

          {/* Content Container */}
          <div className="relative h-full flex flex-col p-6 overflow-y-auto">

            {/* Menu Header */}
            <div className="mb-6">
              <div className="flex items-center space-x-2 mb-2">
                <div className="w-8 h-1 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full"></div>
                <span className="text-xs font-bold text-slate-400 uppercase tracking-widest">Navigation</span>
              </div>
            </div>

            {/* Navigation Links with Icons */}
            <div className="flex flex-col space-y-2 mb-8">
              {[
                { name: 'Home', path: '/', section: null, icon: '🏠', desc: 'Back to homepage' },
                { name: 'Services', path: '/', section: 'analysis-report', icon: '⚡', desc: 'Our analysis tools' },
                { name: 'Subscription', path: '/', section: 'subscription-plans', icon: '💎', desc: 'Premium plans' },
              ].map((link, index) => (
                <button
                  key={link.section || 'home'}
                  onClick={() => handleNavClick(link.path, link.section)}
                  className={`group relative text-left p-4 transition-all duration-300 rounded-2xl overflow-hidden ${isActive(link.path, link.section)
                    ? 'bg-gradient-to-r from-blue-600 to-purple-600 shadow-xl shadow-blue-500/25'
                    : 'bg-white/70 backdrop-blur-sm border border-slate-200/50 hover:border-blue-300 hover:shadow-lg hover:shadow-blue-100'
                    }`}
                  style={{ animationDelay: `${index * 50}ms` }}
                >
                  <div className="flex items-center space-x-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-2xl transition-transform group-hover:scale-110 ${isActive(link.path, link.section)
                      ? 'bg-white/20'
                      : 'bg-gradient-to-br from-blue-50 to-purple-50'
                      }`}>
                      {link.icon}
                    </div>
                    <div className="flex-1">
                      <p className={`text-lg font-bold ${isActive(link.path, link.section) ? 'text-white' : 'text-slate-800'}`}>
                        {link.name}
                      </p>
                      <p className={`text-sm ${isActive(link.path, link.section) ? 'text-white/70' : 'text-slate-500'}`}>
                        {link.desc}
                      </p>
                    </div>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-all ${isActive(link.path, link.section)
                      ? 'bg-white/20 text-white'
                      : 'bg-slate-100 text-slate-400 group-hover:bg-blue-100 group-hover:text-blue-600'
                      }`}>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                  {isActive(link.path, link.section) && (
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent translate-x-[-100%] animate-shimmer"></div>
                  )}
                </button>
              ))}
            </div>

            {/* Divider with label */}
            <div className="flex items-center space-x-3 mb-6">
              <div className="flex-1 h-px bg-gradient-to-r from-transparent via-slate-300 to-transparent"></div>
              <span className="text-xs font-bold text-slate-400 uppercase tracking-widest">Account</span>
              <div className="flex-1 h-px bg-gradient-to-r from-transparent via-slate-300 to-transparent"></div>
            </div>

            {/* Auth / User Section */}
            <div className="mt-auto pb-6">
              {isAuthenticated ? (
                <div className="space-y-4">
                  {/* User Card */}
                  <div className="relative p-5 bg-gradient-to-br from-white to-slate-50 border border-slate-200/50 rounded-3xl shadow-lg overflow-hidden">
                    <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-blue-400/10 to-purple-500/10 rounded-full blur-2xl"></div>
                    <div className="relative flex items-center space-x-4">
                      <UserAvatar 
                        user={user} 
                        size="xl" 
                        shape="square"
                        indicator={true}
                        className="rotate-3 hover:rotate-0 transition-transform"
                        avatarClassName="shadow-purple-500/30"
                      />
                      <div className="flex-1">
                        <p className="font-bold text-slate-900 text-lg">{user?.full_name || user?.email}</p>
                        <div className="mt-1.5">
                          {renderPlanBadge(true)}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <button
                    onClick={() => {
                      navigate('/dashboard');
                      setIsMobileMenuOpen(false);
                    }}
                    className="w-full relative overflow-hidden flex justify-center items-center py-4 px-6 text-white text-lg font-bold bg-gradient-to-r from-blue-600 via-purple-600 to-blue-600 bg-[length:200%_auto] rounded-2xl shadow-xl shadow-purple-500/25 transition-all active:scale-[0.98] hover:shadow-2xl animate-gradient"
                  >
                    <Zap className="w-5 h-5 mr-2.5" />
                    Go to Dashboard
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent translate-x-[-100%] animate-shimmer"></div>
                  </button>

                  {user?.access_role === 'admin' && (
                    <button
                      onClick={() => {
                        navigate('/admin');
                        setIsMobileMenuOpen(false);
                      }}
                      className="w-full flex justify-center items-center py-4 px-6 text-slate-700 text-lg font-bold bg-white border border-slate-200 rounded-2xl shadow-sm hover:bg-slate-50 transition-all"
                    >
                      <Shield className="w-5 h-5 mr-2.5 text-indigo-600" />
                      Admin Portal
                    </button>
                  )}

                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center justify-center space-x-2 py-4 px-6 bg-gradient-to-r from-red-50 to-pink-50 text-red-600 rounded-2xl font-bold border border-red-200 hover:from-red-100 hover:to-pink-100 transition-all"
                  >
                    <LogOut className="w-5 h-5" />
                    <span>Sign Out</span>
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  {/* Welcome Message */}
                  <div className="text-center mb-4 p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl border border-blue-100">
                    <Sparkles className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                    <p className="text-slate-700 font-medium">Join thousands of property analysts</p>
                    <p className="text-sm text-slate-500">Get started for free today</p>
                  </div>

                  <button
                    onClick={handleLogin}
                    className="w-full flex items-center justify-center space-x-2 py-4 px-6 bg-white border-2 border-slate-200 text-slate-700 rounded-2xl hover:bg-slate-50 hover:border-slate-300 transition-all duration-300 font-bold text-lg shadow-sm hover:shadow-md"
                  >
                    <LogIn className="w-5 h-5" />
                    <span>Sign In</span>
                  </button>

                  <button
                    onClick={handleRegister}
                    className="w-full relative overflow-hidden flex items-center justify-center space-x-2 py-4 px-6 bg-gradient-to-r from-blue-600 via-purple-600 to-blue-600 bg-[length:200%_auto] text-white rounded-2xl transition-all duration-300 font-bold shadow-xl shadow-purple-500/25 text-lg active:scale-[0.98] animate-gradient"
                  >
                    <UserPlus className="w-5 h-5" />
                    <span>Create Free Account</span>
                    <Sparkles className="w-5 h-5 animate-pulse" />
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent translate-x-[-100%] animate-shimmer"></div>
                  </button>
                </div>
              )}
            </div>

            {/* Footer Branding */}
            <div className="pt-4 border-t border-slate-200/50 text-center">
              <p className="text-xs text-slate-400">
                © 2024 DevelopmentAnalyzer • <span className="text-blue-500">AI-Powered</span>
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Additional CSS for shimmer animation */}
      <style>{`
        @keyframes shimmer {
          100% { transform: translateX(100%); }
        }
        .animate-shimmer {
          animation: shimmer 2s infinite;
        }
      `}</style>
    </>
  );
};

export default Navbar;